// Notification Settings Page
import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Switch,
  Button,
  Divider,
  Alert,
  Grid,
  Card,
  CardContent,
  Chip,
} from '@mui/material';
import {
  Email,
  NotificationsActive,
  BugReport,
  Lightbulb,
  Article,
  Chat,
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/contexts/ToastContext';
import { usePermissions } from '@/hooks/usePermissions';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '@/services/firebase/config';

const notificationSchema = z.object({
  // Email notifications
  emailOnNewTicket: z.boolean(),
  emailOnTicketReply: z.boolean(),
  emailOnTicketAssigned: z.boolean(),
  emailOnFeatureRequest: z.boolean(),
  emailDigestEnabled: z.boolean(),
  emailDigestFrequency: z.enum(['daily', 'weekly']),
});

type NotificationFormData = z.infer<typeof notificationSchema>;

export const NotificationSettingsPage = () => {
  const { organization, project } = useOrganization();
  const { user } = useAuth();
  const { showSuccess, showError } = useToast();
  const { canManageSettings } = usePermissions();

  const [saving, setSaving] = useState(false);

  const {
    control,
    handleSubmit,
    watch,
    reset,
    formState: { isDirty },
  } = useForm<NotificationFormData>({
    resolver: zodResolver(notificationSchema),
    defaultValues: {
      emailOnNewTicket: true,
      emailOnTicketReply: true,
      emailOnTicketAssigned: true,
      emailOnFeatureRequest: false,
      emailDigestEnabled: false,
      emailDigestFrequency: 'daily',
    },
  });

  const emailDigestEnabled = watch('emailDigestEnabled');

  // Load current settings
  useEffect(() => {
    if (project?.notifications) {
      reset({
        emailOnNewTicket: project.notifications.emailOnNewTicket ?? true,
        emailOnTicketReply: true,
        emailOnTicketAssigned: true,
        emailOnFeatureRequest: project.notifications.emailOnNewFeatureRequest ?? false,
        emailDigestEnabled: false,
        emailDigestFrequency: 'daily',
      });
    }
  }, [project, reset]);

  const onSubmit = async (data: NotificationFormData) => {
    if (!organization || !project || !user) return;

    setSaving(true);
    try {
      const projectRef = doc(db, 'organizations', organization.id, 'projects', project.id);
      await updateDoc(projectRef, {
        'notifications.emailOnNewTicket': data.emailOnNewTicket,
        'notifications.emailOnNewFeatureRequest': data.emailOnFeatureRequest,
        updatedAt: serverTimestamp(),
      });

      showSuccess('Notification settings saved');
    } catch (err) {
      console.error('Error saving notification settings:', err);
      showError('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const canEdit = canManageSettings;

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 900 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          Notification Settings
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Configure how you receive notifications for {project?.name || 'your project'}
        </Typography>
      </Box>

      {!canEdit && (
        <Alert severity="info" sx={{ mb: 3 }}>
          You don't have permission to modify notification settings.
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit(onSubmit)}>
        {/* Email Notifications */}
        <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
            <Email color="primary" />
            <Typography variant="h6" fontWeight="medium">
              Email Notifications
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Receive email notifications for important events
          </Typography>

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Card variant="outlined">
                <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <BugReport sx={{ color: 'error.main' }} />
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="subtitle2">New Tickets</Typography>
                    <Typography variant="caption" color="text.secondary">
                      When a new support ticket is created
                    </Typography>
                  </Box>
                  <Controller
                    name="emailOnNewTicket"
                    control={control}
                    render={({ field }) => (
                      <Switch
                        {...field}
                        checked={field.value}
                        disabled={!canEdit}
                      />
                    )}
                  />
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Card variant="outlined">
                <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Chat sx={{ color: 'primary.main' }} />
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="subtitle2">Ticket Replies</Typography>
                    <Typography variant="caption" color="text.secondary">
                      When a customer replies to a ticket
                    </Typography>
                  </Box>
                  <Controller
                    name="emailOnTicketReply"
                    control={control}
                    render={({ field }) => (
                      <Switch
                        {...field}
                        checked={field.value}
                        disabled={!canEdit}
                      />
                    )}
                  />
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Card variant="outlined">
                <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <NotificationsActive sx={{ color: 'warning.main' }} />
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="subtitle2">Ticket Assigned</Typography>
                    <Typography variant="caption" color="text.secondary">
                      When a ticket is assigned to you
                    </Typography>
                  </Box>
                  <Controller
                    name="emailOnTicketAssigned"
                    control={control}
                    render={({ field }) => (
                      <Switch
                        {...field}
                        checked={field.value}
                        disabled={!canEdit}
                      />
                    )}
                  />
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Card variant="outlined">
                <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Lightbulb sx={{ color: 'success.main' }} />
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="subtitle2">Feature Requests</Typography>
                    <Typography variant="caption" color="text.secondary">
                      When a new feature is requested
                    </Typography>
                  </Box>
                  <Controller
                    name="emailOnFeatureRequest"
                    control={control}
                    render={({ field }) => (
                      <Switch
                        {...field}
                        checked={field.value}
                        disabled={!canEdit}
                      />
                    )}
                  />
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* Email Digest */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Article sx={{ color: 'secondary.main' }} />
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="subtitle2">Email Digest</Typography>
              <Typography variant="caption" color="text.secondary">
                Receive a summary of activity instead of individual emails
              </Typography>
            </Box>
            <Controller
              name="emailDigestEnabled"
              control={control}
              render={({ field }) => (
                <Switch
                  {...field}
                  checked={field.value}
                  disabled={!canEdit}
                />
              )}
            />
          </Box>

          {emailDigestEnabled && (
            <Box sx={{ ml: 5 }}>
              <Controller
                name="emailDigestFrequency"
                control={control}
                render={({ field }) => (
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Chip
                      label="Daily"
                      variant={field.value === 'daily' ? 'filled' : 'outlined'}
                      color={field.value === 'daily' ? 'primary' : 'default'}
                      onClick={() => field.onChange('daily')}
                      disabled={!canEdit}
                    />
                    <Chip
                      label="Weekly"
                      variant={field.value === 'weekly' ? 'filled' : 'outlined'}
                      color={field.value === 'weekly' ? 'primary' : 'default'}
                      onClick={() => field.onChange('weekly')}
                      disabled={!canEdit}
                    />
                  </Box>
                )}
              />
            </Box>
          )}
        </Paper>

        {/* Save Button */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            type="submit"
            variant="contained"
            disabled={saving || !canEdit || !isDirty}
            sx={{
              minWidth: 150,
              background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
              },
            }}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};
